import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileSearch, 
  Target, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  Lightbulb,
  Loader2,
  History,
  Sparkles,
  Award,
  Briefcase,
  Clock,
  Lock,
  FileCheck
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';
import UpgradePrompt from '../components/UpgradePrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ATSScoring = () => {
  const { token } = useAuth();
  const { hasFullAccess, isPreviewMode } = useSubscription();
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (hasFullAccess) {
      fetchHistory();
    } else {
      setLoadingHistory(false);
    }
  }, [token, hasFullAccess]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ats/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const analyzeResume = async () => {
    if (!hasFullAccess) {
      toast.error('Activate your membership to use the ATS Resume Scorer');
      return;
    }

    if (!jobDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/ats/score`, {
        job_description: jobDescription,
        job_title: jobTitle || undefined,
        company_name: companyName || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(response.data);
      fetchHistory();
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze. Please complete your profile first.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-100';
    if (score >= 60) return 'bg-amber-100';
    return 'bg-red-100';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="ats-scoring-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">ATS Resume Scorer</h1>
          <p className="text-navy-600 dark:text-navy-300">Analyze how well your profile matches job descriptions</p>
        </div>

        {/* Preview Mode - Show Upgrade */}
        {isPreviewMode && (
          <div className="mb-8">
            <UpgradePrompt 
              feature="the ATS Resume Scorer"
              title="Unlock ATS Analysis"
              description="Analyze how well your profile matches job descriptions with AI-powered scoring and personalized recommendations."
            />
          </div>
        )}

        {/* Preview Mode Feature Overview */}
        {isPreviewMode && (
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <FileSearch className="w-5 h-5 text-amber-600" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { step: '1', title: 'Paste Job Description', desc: 'Enter the job posting you want to apply for' },
                    { step: '2', title: 'AI Analysis', desc: 'Our AI compares your profile against the requirements' },
                    { step: '3', title: 'Get Actionable Results', desc: 'Receive scores, keyword gaps, and improvement tips' }
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3 p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                      <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{item.step}</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-900 dark:text-white">{item.title}</p>
                        <p className="text-sm text-navy-500 dark:text-navy-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  What You Get
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-navy-600 dark:text-navy-300">
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Overall ATS compatibility score</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Keyword match analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Skills and experience gap identification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Missing keywords to add to your profile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Personalized improvement recommendations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Access Content */}
        {hasFullAccess && (
        <Tabs defaultValue="analyze" className="space-y-6">
          <TabsList>
            <TabsTrigger value="analyze" className="gap-2">
              <FileSearch className="w-4 h-4" />
              Analyze Job
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              History ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analyze">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Input Section */}
              <Card className="border-navy-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-navy-900">
                    <Target className="w-5 h-5 text-amber-600" />
                    Job Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-navy-700">Job Title (Optional)</Label>
                      <Input
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        placeholder="e.g., Project Manager"
                        className="mt-1"
                        data-testid="ats-job-title"
                      />
                    </div>
                    <div>
                      <Label className="text-navy-700">Company (Optional)</Label>
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g., Tech Corp"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-navy-700">Job Description *</Label>
                    <Textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Paste the full job description here..."
                      className="mt-1 min-h-[250px]"
                      data-testid="ats-job-description"
                    />
                    <p className="text-xs text-navy-500 mt-1">
                      Tip: Include the full job posting for best results
                    </p>
                  </div>

                  <Button 
                    onClick={analyzeResume}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={loading || !jobDescription.trim()}
                    data-testid="analyze-ats-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze Match
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results Section */}
              <div className="space-y-6">
                {loading ? (
                  <Card className="border-navy-100">
                    <CardContent className="py-16 text-center">
                      <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
                      <p className="text-navy-700 font-medium">Analyzing your profile...</p>
                      <p className="text-navy-500 text-sm mt-1">This may take a moment</p>
                    </CardContent>
                  </Card>
                ) : result ? (
                  <>
                    {/* Overall Score */}
                    <Card className="border-navy-100 overflow-hidden">
                      <div className={`${getScoreBg(result.overall_score)} p-6`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-navy-600 mb-1">Overall ATS Score</p>
                            <p className={`text-5xl font-bold ${getScoreColor(result.overall_score)}`}>
                              {result.overall_score}%
                            </p>
                          </div>
                          <div className={`w-20 h-20 rounded-full ${getScoreBg(result.overall_score)} border-4 ${
                            result.overall_score >= 80 ? 'border-emerald-500' : 
                            result.overall_score >= 60 ? 'border-amber-500' : 'border-red-500'
                          } flex items-center justify-center`}>
                            <Award className={`w-10 h-10 ${getScoreColor(result.overall_score)}`} />
                          </div>
                        </div>
                        <p className="text-navy-600 mt-3 text-sm">{result.summary}</p>
                      </div>
                    </Card>

                    {/* Score Breakdown */}
                    <Card className="border-navy-100">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base text-navy-900">Score Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-navy-600">Keyword Match</span>
                            <span className={`font-semibold ${getScoreColor(result.keyword_match_score)}`}>
                              {result.keyword_match_score}%
                            </span>
                          </div>
                          <div className="h-2 bg-navy-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressColor(result.keyword_match_score)} transition-all`}
                              style={{ width: `${result.keyword_match_score}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-navy-600">Skills Match</span>
                            <span className={`font-semibold ${getScoreColor(result.skills_match_score)}`}>
                              {result.skills_match_score}%
                            </span>
                          </div>
                          <div className="h-2 bg-navy-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressColor(result.skills_match_score)} transition-all`}
                              style={{ width: `${result.skills_match_score}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-navy-600">Experience Match</span>
                            <span className={`font-semibold ${getScoreColor(result.experience_match_score)}`}>
                              {result.experience_match_score}%
                            </span>
                          </div>
                          <div className="h-2 bg-navy-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressColor(result.experience_match_score)} transition-all`}
                              style={{ width: `${result.experience_match_score}%` }}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Strengths & Improvements */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Card className="border-navy-100">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
                            <CheckCircle className="w-4 h-4" />
                            Strengths
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {result.strengths?.map((strength, idx) => (
                              <li key={idx} className="text-sm text-navy-600 flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>

                      <Card className="border-navy-100">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                            <TrendingUp className="w-4 h-4" />
                            Areas to Improve
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {result.improvements?.map((improvement, idx) => (
                              <li key={idx} className="text-sm text-navy-600 flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                {improvement}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Missing Keywords */}
                    {result.missing_keywords?.length > 0 && (
                      <Card className="border-navy-100">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            Missing Keywords
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {result.missing_keywords.map((keyword, idx) => (
                              <Badge key={idx} variant="outline" className="border-red-200 text-red-600 bg-red-50">
                                {keyword}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-navy-500 mt-3">
                            Consider adding these keywords to your profile or resume if applicable
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Recommendations */}
                    <Card className="border-navy-100">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                          <Lightbulb className="w-4 h-4" />
                          Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-3">
                          {result.recommendations?.map((rec, idx) => (
                            <li key={idx} className="text-sm text-navy-600 flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                {idx + 1}
                              </span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card className="border-navy-100">
                    <CardContent className="py-16 text-center">
                      <FileSearch className="w-16 h-16 text-navy-200 mx-auto mb-4" />
                      <h3 className="font-heading font-semibold text-navy-700 mb-2">
                        Ready to Analyze
                      </h3>
                      <p className="text-navy-500 max-w-sm mx-auto">
                        Paste a job description and click "Analyze Match" to see how well your profile aligns with the requirements
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            {loadingHistory ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              </div>
            ) : history.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {history.map((analysis) => (
                  <Card key={analysis.analysis_id} className="border-navy-100 hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-navy-900 truncate">
                            {analysis.job_title || 'Job Analysis'}
                          </h3>
                          {analysis.company_name && (
                            <p className="text-sm text-navy-500 flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {analysis.company_name}
                            </p>
                          )}
                        </div>
                        <div className={`w-14 h-14 rounded-full ${getScoreBg(analysis.overall_score)} flex items-center justify-center`}>
                          <span className={`text-lg font-bold ${getScoreColor(analysis.overall_score)}`}>
                            {analysis.overall_score}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-navy-400">
                        <Clock className="w-3 h-3" />
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-navy-100">
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-navy-200 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-navy-700 mb-2">No analyses yet</h3>
                  <p className="text-navy-500">Your ATS analysis history will appear here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ATSScoring;
